/** Cognito's direct sign-in API, called from our own page (MVP-G6). Plain JSON over the
 *  regional endpoint; no SDK. Password goes to Cognito only, over TLS, and is never kept.
 *  The hosted page (pkce.ts) remains the fallback for anything this does not cover. */

import { saveTokens, type TokenSet } from "./pkce";

type Target =
  | "InitiateAuth"
  | "RespondToAuthChallenge"
  | "ForgotPassword"
  | "ConfirmForgotPassword"
  | "GlobalSignOut";

interface CognitoError {
  __type?: string;
  message?: string;
}

/** What the user sees for each thing Cognito can say. Generic on purpose for the two
 *  that would otherwise confirm whether an address exists. */
export function describe(code: string | undefined, fallback: string): string {
  switch (code) {
    case "NotAuthorizedException":
    case "UserNotFoundException":
      return "That e-mail and password did not match.";
    case "UserNotConfirmedException":
      return "This login has not finished being set up. Use the invitation e-mail, or ask your administrator to resend it.";
    case "PasswordResetRequiredException":
      return "A password reset is required for this login. Use “Forgot your password?” below.";
    case "InvalidPasswordException":
      return "Passwords need at least 12 characters with upper and lower case, a number and a symbol.";
    case "CodeMismatchException":
      return "That code is not right. Check the e-mail and try again.";
    case "ExpiredCodeException":
      return "That code has expired. Request a new one.";
    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Too many attempts. Wait a few minutes and try again.";
    case "InvalidParameterException":
      return "Something in the form is not right. Check the fields and try again.";
    default:
      return fallback;
  }
}

async function call<T>(region: string, target: Target, body: unknown): Promise<T> {
  const res = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "content-type": "application/x-amz-json-1.1",
      "x-amz-target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as T & CognitoError;
  if (!res.ok) {
    const code = (json.__type ?? "").split("#").pop();
    const err = new Error(describe(code, json.message ?? `Sign-in failed (${res.status})`));
    (err as Error & { code?: string }).code = code;
    throw err;
  }
  return json;
}

interface AuthResult {
  AccessToken: string;
  IdToken: string;
  RefreshToken?: string;
  ExpiresIn: number;
}

interface AuthResponse {
  AuthenticationResult?: AuthResult;
  ChallengeName?: "NEW_PASSWORD_REQUIRED" | "SOFTWARE_TOKEN_MFA" | "SMS_MFA" | string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
}

/** Either tokens, or a challenge the page has to answer next. */
export type SignInStep =
  | { kind: "done"; tokens: TokenSet }
  | { kind: "new_password"; session: string; email: string }
  | { kind: "mfa"; session: string; email: string };

function toTokens(r: AuthResult, previousRefresh?: string): TokenSet {
  const t: TokenSet = {
    id_token: r.IdToken,
    access_token: r.AccessToken,
    refresh_token: r.RefreshToken ?? previousRefresh,
    expires_at: Date.now() + r.ExpiresIn * 1000,
  };
  saveTokens(t);
  return t;
}

function step(email: string, r: AuthResponse): SignInStep {
  if (r.AuthenticationResult) return { kind: "done", tokens: toTokens(r.AuthenticationResult) };
  if (r.ChallengeName === "NEW_PASSWORD_REQUIRED" && r.Session) return { kind: "new_password", session: r.Session, email };
  if ((r.ChallengeName === "SOFTWARE_TOKEN_MFA" || r.ChallengeName === "SMS_MFA") && r.Session) return { kind: "mfa", session: r.Session, email };
  throw new Error(`Sign-in needs a step this page does not support (${r.ChallengeName ?? "unknown"}). Use the fallback sign-in below.`);
}

export async function signIn(region: string, clientId: string, email: string, password: string): Promise<SignInStep> {
  const r = await call<AuthResponse>(region, "InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    ClientId: clientId,
    AuthParameters: { USERNAME: email.trim().toLowerCase(), PASSWORD: password },
  });
  return step(email, r);
}

/** First sign-in with the invitation's temporary password. */
export async function completeNewPassword(region: string, clientId: string, email: string, session: string, newPassword: string): Promise<SignInStep> {
  const r = await call<AuthResponse>(region, "RespondToAuthChallenge", {
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    ClientId: clientId,
    Session: session,
    ChallengeResponses: { USERNAME: email.trim().toLowerCase(), NEW_PASSWORD: newPassword },
  });
  return step(email, r);
}

export async function answerMfa(region: string, clientId: string, email: string, session: string, code: string): Promise<SignInStep> {
  const r = await call<AuthResponse>(region, "RespondToAuthChallenge", {
    ChallengeName: "SOFTWARE_TOKEN_MFA",
    ClientId: clientId,
    Session: session,
    ChallengeResponses: { USERNAME: email.trim().toLowerCase(), SOFTWARE_TOKEN_MFA_CODE: code.trim() },
  });
  return step(email, r);
}

export async function forgotPassword(region: string, clientId: string, email: string): Promise<void> {
  await call(region, "ForgotPassword", { ClientId: clientId, Username: email.trim().toLowerCase() });
}

export async function confirmForgotPassword(region: string, clientId: string, email: string, code: string, newPassword: string): Promise<void> {
  await call(region, "ConfirmForgotPassword", {
    ClientId: clientId,
    Username: email.trim().toLowerCase(),
    ConfirmationCode: code.trim(),
    Password: newPassword,
  });
}

/** Best effort: revokes the refresh token server-side. Local tokens are cleared regardless. */
export async function globalSignOut(region: string, accessToken: string): Promise<void> {
  try {
    await call(region, "GlobalSignOut", { AccessToken: accessToken });
  } catch {
    // Already expired or revoked: nothing to do.
  }
}
